// Per-customer note — Phase 6d.
//
// One text blob per customer_email. Rendered in the Order Context sidebar.
// Reads via anon Supabase client (RLS allows allowlisted users to SELECT).
// Writes are upserts — if the row doesn't exist yet, we insert; otherwise
// we update the existing one. Empty-string note is valid and means
// "no note" — we don't delete the row because it's cheap to carry and
// simplifies the caching key.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type CustomerNote = {
  customer_email: string;
  note: string;
  updated_at: string | null;
};

function keyFor(email: string | null | undefined) {
  return ['customer-note', email ?? 'none'] as const;
}

export function useCustomerNote(email: string | null | undefined) {
  return useQuery({
    queryKey: keyFor(email),
    enabled: !!email,
    queryFn: async (): Promise<CustomerNote | null> => {
      if (!email) return null;
      const { data, error } = await supabase
        .from('customer_notes')
        .select('customer_email, note, updated_at')
        .eq('customer_email', email)
        .maybeSingle();
      if (error) throw error;
      return (data as CustomerNote | null) ?? null;
    },
    staleTime: 30_000,
  });
}

export function useSaveCustomerNote(email: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (note: string) => {
      if (!email) throw new Error('Missing customer email');
      // Upsert by primary key. Empty-string note is valid.
      const { error } = await supabase
        .from('customer_notes')
        .upsert(
          { customer_email: email, note },
          { onConflict: 'customer_email' },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyFor(email) });
    },
  });
}
